import {FormGroup} from "@angular/forms";
import {QuickFormGroup} from "../helpers/quick-form-group";
import {ContactModel, contactModelFactory} from "../contact/contact.model";
import gql from "graphql-tag";
import {GqlModel, GqlModelOperator, GqlMutationModel, GqlQueryModel} from "../graphql/graphql.model";
import {
  AvailableStandaloneAccessoryModel,
  StandaloneAccessoryGqlModel,
  StandaloneAccessoryInputDataGqlModel,
} from "../accessory/standalone-accessory.gql-model";
import {SoftwareInputDataGqlModel} from "../software/software.gql-model";
import {JSONModel, jsonProperty} from "../json.model";
import {AddressModel, addressModelFactory} from "../address/address.model";

export class AssetBundledAccessoryModel extends JSONModel {
  @jsonProperty("string") productNumber: string;
  @jsonProperty("string") serialNumber: string;
  @jsonProperty("string") productDescription: string;
  @jsonProperty("boolean") changed: boolean;
}

export class AssetAvailableBulkOrderItemModel extends JSONModel {
  @jsonProperty("string") id: string;
  @jsonProperty("string") orderNumber: string;
  @jsonProperty("string") orderDate: string;
  @jsonProperty("string") quantity: string;
  @jsonProperty("string") quantityAvailable: string;
}

export class AssetAvailableProductModel extends JSONModel {
  @jsonProperty("string") productId: string;
  @jsonProperty("string") productNumber: string;
  @jsonProperty("string") modelName: string;
  @jsonProperty("string") familyName: string;
  @jsonProperty("string") manufacturer: string;
}

export class AssetDispositionModel extends JSONModel {
  @jsonProperty("string") id: string;
  @jsonProperty("string") dispositionText: string;
}

export class AssetAvailableDeviceModel extends JSONModel {
  @jsonProperty("string") id: string;
  @jsonProperty("boolean") installed: boolean;
  @jsonProperty("string") deviceKey: string;
  @jsonProperty("string") originalSolutionKey: string;
  @jsonProperty("string") productNumber: string;  
  @jsonProperty("string") baseCharge: number;
  @jsonProperty("string") friendlyName: string;
  @jsonProperty("string") productDescription: string;
  @jsonProperty("string") masterKey: string;
  @jsonProperty("string") externalId: string;
  @jsonProperty("string") internalId: string;
  @jsonProperty("string") currentOnboardingTransactionId: string;
  @jsonProperty("string") assetCOADocumentLink: string;
  @jsonProperty("string") statusKey: string;
  @jsonProperty([AssetAvailableBulkOrderItemModel]) availableBulkOrderItems: AssetAvailableBulkOrderItemModel[];
  @jsonProperty([AssetAvailableProductModel]) availableProducts: AssetAvailableProductModel[];
  @jsonProperty([AssetBundledAccessoryModel]) bundledAccessories: AssetBundledAccessoryModel[];
  @jsonProperty(AssetDispositionModel) disposition: AssetDispositionModel;
}

export class AssetLocalizationCodeModel extends JSONModel {
  @jsonProperty("string") id: string;
  @jsonProperty("string") localizationCode: string;
  @jsonProperty("string") localizationDescription: string;
}

export class AssetGqlModel extends GqlQueryModel {
  static mapper: GqlModelOperator<AssetGqlModel> =
    GqlModel.buildMapper(AssetGqlModel, "asset");

  static query: any = gql`
    fragment contactDetails on Contact {
      firstName
      lastName
      phoneNumber
      phoneNumber2
      emailAddress
      locale
    }

    fragment bulkOrderDetails on BulkOrderLineItem {
      id
      orderNumber
      orderDate
      quantity
      quantityAvailable
    }

    fragment productDetails on ITSMProduct {
      productId
      familyName
      productNumber
      modelName
    }

    fragment dispositionDetails on Disposition {
      dispositionText
      isFutureFleet
      id
    }

    fragment bundledAccessoryDetails on BundledAccessory {
      productDescription
      productNumber
    }
    
    fragment addressDetails on Address {
      name1
      name2
      line1
      line2
      city
      region
      postalCode
      country
    }
    
    fragment localizationCodeDetails on LocalizationCodeType {
      id
      localizationCode
      localizationDescription
    }

    query getAsset($assetId: ID!) {
      asset: asset(assetId: $assetId) {
        id
        siteId
        deviceId
        purchaseOrderId
        serialNumber
        family
        installDate
        deviceEndDate
        installed
        masterAgreementStartDate
        updateDate
        originalInstallDate
        assetTag
        clinNumber
        itsmConsumableAutoFulfillment
        itsmLongLifeConsumableAutoFulfillment
        itsmMaintenanceAutoFulfillment
        itsmCostCenterId
        changeOrderId
        building
        category
        deliveryTypeModifier
        deliveryLocationModifier
        partsLogisticsSuppliesModifier
        coverageWindowModifier
        responseTimeModifier
        repairTimeModifier
        expressExchange
        depotRepair
        supportType
        healthCheck
        directionsToPrinter
        floor
        floorMapId
        department
        userDetails
        ipAddress
        gateway
        subnet
        hostName
        macAddress
        grid
        installDate
        assetServiceAddressId
        serviceAddress {
          ...addressDetails
        }
        assetTonerAddressId
        tonerAddress {
          ...addressDetails
        }
        assetBreakfixContactId
        assetTonerContactId
        assetInstallationContactId
        siteBreakfixContactId
        billToContactId 
        invoiceContactId
        hpInvoiceContactId
        hpContractAdminContactId
        hpAccDeliveryMgrContactId
        hpSalesRepContactId
        assetBillToContactId 
        assetInvoiceContactId
        assetHpInvoiceContactId
        assetHpContractAdminContactId
        assetHpAccDeliveryMgrContactId
        assetHpSalesRepContactId
        toner_contact_id
        installation_contact_id
        breakfix_contact_id
        order_status_notification_1
        order_status_notification_2
        siteOrderStatusNotification1
        siteOrderStatusNotification2
        siteTonerContactId
        siteInstallationContactId
        siteTonerAddressId
        siteServiceAddressId
        itsmSiteId
        itsmSiteName
        itsmBuildingName
        printQueue
        faxNumber
        leaseId
        trackingNumber
        status
        area_group_id
        serviceLevel
        connectType
        collectMethod
        initialPageCount
        initialPageCountColor
        initialPageCountColorProfessional
        initialPageCountColorAccent
        initialPageCountColorGeneralOffice
        initialPageCountOther
        comments
        defaultCustomShippingLabel1
        defaultCustomShippingLabel2
        hpInstalledKit
        disposition {
          ...dispositionDetails
        }
        dispositionId
        deviceKey
        originalSolutionKey
        productId
        productNumber
        modelName
        manufacturer
        orderDate
        orderNumber
        bulkOrderAssetId
        eotLeaseDate
        sourceName
        caseNumber
        replacementProductNumber
        replacementProductSerial
        customHeading1
        customHeading2
        customHeading3
        customHeading4
        customHeading5
        custom1
        custom2
        custom3
        custom4
        custom5
        custom6
        custom7
        custom8
        custom9
        custom10
        contractAccountId
        billingModel
        workOrderEmailAddress
        renewal
        legacyDocumentNumber
        legacyReportedDate
        legacyDocumentType
        prepaidFieldOnDorTab
        multiYearPoType
        multiYearTermDuration
        previousDocument
        localizationCodeId
        printCapability
        availableLocalizationCodes {
          ...localizationCodeDetails
        }
        dispositions {
          ...dispositionDetails
        }
        availableProducts {
          ...productDetails
        }
        software {
          id
          siteId
          assetId
          assetSerialNumber
          deviceId
          deviceKey
          originalSolutionKey
          productNumber
          productFamily
          productDescription
          installDate
          deviceEndDate
          contractAccountId
          installed
          solutionsProductNumber
          productSold
          serialNumber
          changeOrderId
          licenseNumber
          customHeader1
          customHeader2
          priceBase
          acquisitionMethod
          externalId
          internalId
          currentOnboardingTransactionId
        }
        standaloneAccessories {
          id
          assetId
          assetSerialNumber
          siteId
          deviceKey
          originalSolutionKey
          deviceId
          productNumber
          productDescription
          serialNumber
          installDate
          deviceEndDate
          contractAccountId
          installed
          building
          floor
          orderNumber
          orderDate
          bulkOrderAccessoryId
          availableBulkOrderItems {
            ...bulkOrderDetails
          }
          priceBase
          acquisitionMethod
          externalId
          internalId
          currentOnboardingTransactionId
        }
        availableStandaloneAccessories {
          id
          deviceKey
          originalSolutionKey
          productNumber
          productDescription
          availableBulkOrderItems {
            ...bulkOrderDetails
          }
        }
        bundledAccessories {
          ...bundledAccessoryDetails
          serialNumber
        }
        availableBulkOrderItems {
          ...bulkOrderDetails
        }
        tonerContact {
          ...contactDetails
        }
        installationContact {
          ...contactDetails
        }
        breakfixContact {
          ...contactDetails
        }
        availableDevices {
          id
          deviceKey
          originalSolutionKey
          productNumber
          productDescription
          baseCharge
          friendlyName
          masterKey
          disposition {
            ...dispositionDetails
          }
          availableBulkOrderItems {
            ...bulkOrderDetails
          }
          availableProducts {
            ...productDetails
          }
          bundledAccessories {
            ...bundledAccessoryDetails
          }
        }
        billFromBPIDId
        acquisitionMethod
        externalId
        internalId
        currentOnboardingTransactionId
        assetCOADocumentLink
        statusKey
      }
    }
  `;

  @jsonProperty("string") id: string;
  @jsonProperty("string") siteId: string;
  @jsonProperty("string") deviceId: string;
  @jsonProperty("string") purchaseOrderId: string;
  @jsonProperty("boolean") installed: boolean;
  @jsonProperty("string") serialNumber: string;
  @jsonProperty("string") family: string;
  @jsonProperty("string") assetTag: string;
  @jsonProperty("string") clinNumber: string;
  @jsonProperty("string") building: string;
  @jsonProperty("string") floor: string;
  @jsonProperty("string") floorMapId: string;
  @jsonProperty("string") department: string;
  @jsonProperty("string") ipAddress: string;
  @jsonProperty("stringDate") originalInstallDate: string;
  @jsonProperty("string") gateway: string;
  @jsonProperty("string") subnet: string;
  @jsonProperty("string") hostName: string;
  @jsonProperty("string") macAddress: string;
  @jsonProperty("stringDate") installDate: string;
  @jsonProperty("stringDate") deviceEndDate: string;
  @jsonProperty("stringDate") updateDate: string;
  @jsonProperty("string") grid: string;
  @jsonProperty("boolean", "disposition.isFutureFleet") isFutureFleet: boolean;
  @jsonProperty("string") dispositionId: string;
  @jsonProperty("string", "disposition.dispositionText") dispositionText: string;
  @jsonProperty("string") deviceKey: string;
  @jsonProperty("string") originalSolutionKey: string;
  @jsonProperty("string") productId: string;
  @jsonProperty("string") productNumber: string;
  @jsonProperty("string") modelName: string;
  @jsonProperty("string") manufacturer: string;
  @jsonProperty("string") orderDate: string;
  @jsonProperty("string") orderNumber: string;
  @jsonProperty("string") connectType: string;
  @jsonProperty("string") collectMethod: string;
  @jsonProperty("string") initialPageCount: string;
  @jsonProperty("string") initialPageCountColor: string;
  @jsonProperty("string") initialPageCountColorProfessional: string;
  @jsonProperty("string") initialPageCountColorAccent: string;
  @jsonProperty("string") initialPageCountColorGeneralOffice: string;
  @jsonProperty("string") initialPageCountOther: string;
  @jsonProperty("string") comments: string;
  @jsonProperty("string") bulkOrderAssetId: string;
  @jsonProperty("boolean", "bulkOrderAssetId") useBulkOrdering: boolean;
  @jsonProperty("stringDate") eotLeaseDate: string;
  @jsonProperty("stringDate") masterAgreementStartDate: string;
  @jsonProperty("string") sourceName: string;
  @jsonProperty("string") caseNumber: string;
  @jsonProperty("string") replacementProductNumber: string;
  @jsonProperty("string") replacementProductSerial: string;
  @jsonProperty("string") customHeading1: string;
  @jsonProperty("string") customHeading2: string;
  @jsonProperty("string") customHeading3: string;
  @jsonProperty("string") customHeading4: string;
  @jsonProperty("string") customHeading5: string;
  @jsonProperty("string") custom1: string;
  @jsonProperty("string") custom2: string;
  @jsonProperty("string") custom3: string;
  @jsonProperty("string") custom4: string;
  @jsonProperty("string") custom5: string;
  @jsonProperty("string") custom6: string;
  @jsonProperty("string") custom7: string;
  @jsonProperty("string") custom8: string;
  @jsonProperty("string") custom9: string;
  @jsonProperty("string") custom10: string;
  @jsonProperty("string") contractAccountId: string;
  @jsonProperty("string") billingModel: string;
  @jsonProperty("string") workOrderEmailAddress: string;
  @jsonProperty("string") renewal: string;
  @jsonProperty("string") legacyDocumentNumber: string;
  @jsonProperty("string") legacyReportedDate: string;
  @jsonProperty("string") legacyDocumentType: string;
  @jsonProperty("string") prepaidFieldOnDorTab: string;
  @jsonProperty("string") multiYearPoType: string;
  @jsonProperty("string") multiYearTermDuration: string;
  @jsonProperty("string") previousDocument: string;
  @jsonProperty("string") geoRegionId: string;
  @jsonProperty("string") category: string;
  @jsonProperty("string") deliveryTypeModifier: string;
  @jsonProperty("string") deliveryLocationModifier: string;
  @jsonProperty("string") partsLogisticsSuppliesModifier: string;
  @jsonProperty("string") coverageWindowModifier: string;
  @jsonProperty("string") responseTimeModifier: string;
  @jsonProperty("string") repairTimeModifier: string;
  @jsonProperty("string") expressExchange: string;
  @jsonProperty("string") depotRepair: string;
  @jsonProperty("string") supportType: string;
  @jsonProperty("string") healthCheck: string;
  @jsonProperty("string") directionsToPrinter: string;
  @jsonProperty("boolean") hpInstalledKit: boolean;
  @jsonProperty("boolean") itsmConsumableAutoFulfillment: boolean;
  @jsonProperty("boolean") itsmLongLifeConsumableAutoFulfillment: boolean;
  @jsonProperty("boolean") itsmMaintenanceAutoFulfillment: boolean;
  @jsonProperty([StandaloneAccessoryGqlModel]) standaloneAccessories: StandaloneAccessoryGqlModel[];
  @jsonProperty([SoftwareInputDataGqlModel]) software: SoftwareInputDataGqlModel[];
  @jsonProperty([AssetBundledAccessoryModel]) bundledAccessories: AssetBundledAccessoryModel[];
  @jsonProperty([AssetAvailableBulkOrderItemModel]) availableBulkOrderItems: AssetAvailableBulkOrderItemModel[];
  @jsonProperty([AssetAvailableProductModel]) availableProducts: AssetAvailableProductModel[];

  @jsonProperty(contactModelFactory) tonerContact: ContactModel;
  @jsonProperty(contactModelFactory) installationContact: ContactModel;
  @jsonProperty(contactModelFactory) breakfixContact: ContactModel;

  @jsonProperty(addressModelFactory) serviceAddress: AddressModel;
  @jsonProperty(addressModelFactory) tonerAddress: AddressModel;
  @jsonProperty([AssetDispositionModel]) dispositions: AssetDispositionModel[];

  @jsonProperty("string") billToContactId: string;
  @jsonProperty("string") invoiceContactId: string;
  @jsonProperty("string") hpInvoiceContactId: string;
  @jsonProperty("string") assetServiceAddressId: string;
  @jsonProperty("string") hpContractAdminContactId: string;
  @jsonProperty("string") hpAccDeliveryMgrContactId: string;
  @jsonProperty("string") hpSalesRepContactId: string;
  @jsonProperty("string") assetBillToContactId: string;
  @jsonProperty("string") assetInvoiceContactId: string;
  @jsonProperty("string") assetHpInvoiceContactId: string;
  @jsonProperty("string") assetHpContractAdminContactId: string;
  @jsonProperty("string") assetHpAccDeliveryMgrContactId: string;
  @jsonProperty("string") assetHpSalesRepContactId: string;
  @jsonProperty("string") assetTonerAddressId: string;
  @jsonProperty("string") assetBreakfixContactId: string;
  @jsonProperty("string") assetTonerContactId: string;
  @jsonProperty("string") assetInstallationContactId: string;
  @jsonProperty("string") siteBreakfixContactId: string;
  @jsonProperty("string") toner_contact_id: string;
  @jsonProperty("string") installation_contact_id: string;
  @jsonProperty("string") breakfix_contact_id: string;
  @jsonProperty("string") order_status_notification_1: string;
  @jsonProperty("string") order_status_notification_2: string;
  @jsonProperty("string") siteOrderStatusNotification1: string;
  @jsonProperty("string") siteOrderStatusNotification2: string;
  @jsonProperty("string") siteTonerContactId: string;
  @jsonProperty("string") siteInstallationContactId: string;
  @jsonProperty("string") siteTonerAddressId: string;
  @jsonProperty("string") siteServiceAddressId: string;
  @jsonProperty("string") itsmSiteId: string;
  @jsonProperty("string") itsmSiteName: string;
  @jsonProperty("string") itsmBuildingName: string;
  @jsonProperty("string") printQueue: string;
  @jsonProperty("string") faxNumber: string;
  @jsonProperty("string") leaseId: string;
  @jsonProperty("string") trackingNumber: string;
  @jsonProperty("string") status: string;
  @jsonProperty("string") area_group_id: string;
  @jsonProperty("string") serviceLevel: string;
  @jsonProperty("string") defaultCustomShippingLabel1: string;
  @jsonProperty("string") defaultCustomShippingLabel2: string;
  @jsonProperty("string") localizationCodeId: string;
  @jsonProperty("string") userDetails: string;

  @jsonProperty([AssetLocalizationCodeModel]) availableLocalizationCodes: AssetLocalizationCodeModel[];
  @jsonProperty([AssetAvailableDeviceModel])
  availableDevices: AssetAvailableDeviceModel[];

  @jsonProperty([AvailableStandaloneAccessoryModel])
  availableStandaloneAccessories: AvailableStandaloneAccessoryModel[];

  @jsonProperty("string") itsmCostCenterId: string;
  @jsonProperty("string") changeOrderId: string;

  @jsonProperty("string") billFromBPIDId?: string; // only for ui tracking of value
  @jsonProperty("string") acquisitionMethod: string;
  @jsonProperty("string") printCapability: string;
  @jsonProperty("string") externalId: string;
  @jsonProperty("string") internalId: string;
  @jsonProperty("string") currentOnboardingTransactionId: string;
  @jsonProperty("string") assetCOADocumentLink: string;
  @jsonProperty("string") statusKey: string;
  formGroup: FormGroup;

  static variables(assetId: string) {
    return {assetId};
  }

  constructor(data: any) {
    super(data);

    for (const accessory of this.standaloneAccessories) {
      accessory.original = true;
      accessory.keep = true;
      accessory.changed = false;
    }

    for (const accessory of this.bundledAccessories) {
      accessory.changed = false;
    }

    this.formGroup = new QuickFormGroup(this, [
      "id",
      "deviceId",
      "installed",
      "serialNumber",
      "purchaseOrderId",
      "assetTag",
      "clinNumber",
      "building",
      "floor",
      "itsmConsumableAutoFulfillment",
      "itsmLongLifeConsumableAutoFulfillment",
      "itsmMaintenanceAutoFulfillment",
      "floorMapId",
      "department",
      "toner_contact_id",
      "installation_contact_id",
      "order_status_notification_1",
      "order_status_notification_2",
      "breakfix_contact_id",
      "ipAddress",
      "gateway",
      "subnet",
      "hostName",
      "category",
      "deliveryTypeModifier",
      "deliveryLocationModifier",
      "partsLogisticsSuppliesModifier",
      "coverageWindowModifier",
      "responseTimeModifier",
      "repairTimeModifier",
      "expressExchange",
      "depotRepair",
      "supportType",
      "healthCheck",
      "directionsToPrinter",
      "macAddress",
      "printQueue",
      "faxNumber",
      "leaseId",
      "trackingNumber",
      "grid",
      "status",
      "area_group_id",
      "serviceLevel",
      "connectType",
      "collectMethod",
      "initialPageCount",
      "initialPageCountColor",
      "initialPageCountColorProfessional",
      "initialPageCountColorAccent",
      "initialPageCountColorGeneralOffice",
      "initialPageCountOther",
      "dispositionId",
      "deviceKey",
      "originalSolutionKey",
      "originalInstallDate",
      "updateDate",
      "productId",
      "productNumber",
      "modelName",
      "manufacturer",
      "orderDate",
      "orderNumber",
      "bulkOrderAssetId",
      "useBulkOrdering",
      "eotLeaseDate",
      "sourceName",
      "caseNumber",
      "replacementProductNumber",
      "replacementProductSerial",
      "installDate",
      "deviceEndDate",
      "hpInstalledKit",
      "comments",
      "custom1",
      "custom2",
      "custom3",
      "custom4",
      "custom5",
      "custom6",
      "custom7",
      "custom8",
      "custom9",
      "custom10",
      "contractAccountId",
      "contractAccountName",
      "software",
      "workOrderEmailAddress",
      "renewal",
      "legacyDocumentNumber",
      "legacyReportedDate",
      "legacyDocumentType",
      "prepaidFieldOnDorTab",
      "multiYearPoType",
      "multiYearTermDuration",
      "previousDocument",
      "localizationCodeId",
      "itsmCostCenterId",
      "changeOrderId",
      "userDetails",
      "billFromBPIDId",
      "acquisitionMethod",
      "assetCOADocumentLink",
      "statusKey",
    ]);
  }
}

export class AssetInputResponseGqlModel extends GqlModel {
  @jsonProperty("string") id: string;
  @jsonProperty("boolean") updated: boolean;
}

export class AssetInputGqlModel extends GqlMutationModel {
  static mapper: GqlModelOperator<AssetInputResponseGqlModel> =
    GqlModel.buildMapper(AssetInputResponseGqlModel, "asset");

  static allKeys: (keyof (AssetInputGqlModel & AssetGqlModel))[] = [
    "serialNumber",
    "assetTag",
    "clinNumber",
    "purchaseOrderId",
    "building",
    "floor",
    "floorMapId",
    "department",
    "toner_contact_id",
    "installation_contact_id",
    "breakfix_contact_id",
    "order_status_notification_1",
    "order_status_notification_2",
    "ipAddress",
    "gateway",
    "subnet",
    "hostName",
    "macAddress",
    "grid",
    "printQueue",
    "faxNumber",
    "leaseId",
    "trackingNumber",
    "status",
    "area_group_id",
    "installDate",
    "deviceEndDate",
    "installed",
    "hpInstalledKit",
    "itsmConsumableAutoFulfillment",
    "itsmLongLifeConsumableAutoFulfillment",
    "itsmMaintenanceAutoFulfillment",
    "itsmCostCenterId",
    "changeOrderId",
    "connectType",
    "collectMethod",
    "initialPageCount",
    "initialPageCountColor",
    "initialPageCountColorProfessional",
    "initialPageCountColorAccent",
    "initialPageCountColorGeneralOffice",
    "initialPageCountOther",
    "comments",
    "category",
    "workOrderEmailAddress",
    "deliveryTypeModifier",
    "deliveryLocationModifier",
    "partsLogisticsSuppliesModifier",
    "coverageWindowModifier",
    "responseTimeModifier",
    "repairTimeModifier",
    "expressExchange",
    "depotRepair",
    "supportType",
    "healthCheck",
    "directionsToPrinter",
    "software",
    "renewal",
    "legacyDocumentNumber",
    "legacyReportedDate",
    "legacyDocumentType",
    "prepaidFieldOnDorTab",
    "multiYearPoType",
    "multiYearTermDuration",
    "previousDocument",
    "localizationCodeId",
    "userDetails",
    "contractAccountId",
    "billFromBPIDId",
    "acquisitionMethod",
    "assetCOADocumentLink",
    "statusKey",
  ];

  static futureKeys: (keyof AssetGqlModel)[] = [
    "deviceId",
    "orderDate",
    "orderNumber",
    "productId",
    "eotLeaseDate",
    "sourceName",
    "bulkOrderAssetId",
    "custom1",
    "custom2",
    "custom3",
    "custom4",
    "custom5",
    "custom6",
    "custom7",
    "custom8",
    "custom9",
    "custom10",
  ];

  static removalKeys: (keyof AssetGqlModel)[] = [
    "productNumber",
    "modelName",
    "manufacturer",
    "dispositionId",
    "caseNumber",
    "replacementProductNumber",
    "replacementProductSerial",
  ];

  static dorDataFields: (keyof AssetGqlModel)[] = [
    "renewal",
    "legacyDocumentNumber",
    "legacyReportedDate",
    "legacyDocumentType",
    "prepaidFieldOnDorTab",
    "multiYearPoType",
    "multiYearTermDuration",
    "previousDocument",
  ];

  static query = gql`
    mutation updateAsset($assetId: ID!, $asset: AssetInput!) {
      asset: asset(assetId: $assetId, asset: $asset) {
        id
        updated
      }
    }
  `;

  @jsonProperty("string") assetId: string;
  @jsonProperty(AssetInputGqlModel.makeAssetModel) asset: {[key: string]: any};

  static makeAssetModel(asset: AssetGqlModel) {
    const assetData: any = {};

    for (const key of AssetInputGqlModel.allKeys) {
      if (asset.formGroup.controls[key].dirty) {
        assetData[key] = asset[key];
      }
    }

    if (asset.isFutureFleet) {
      for (const key of AssetInputGqlModel.futureKeys) {
        if (asset.formGroup.controls[key].dirty) {
          assetData[key] = asset[key];
        }
      }

      if (asset.standaloneAccessories.length) {
        const change = [];
        const remove = [];

        for (const accessory of asset.standaloneAccessories) {
          if (!accessory.keep && accessory.id) {
            remove.push(accessory.id);
          }

          if (accessory.changed) {
            change.push(new StandaloneAccessoryInputDataGqlModel(accessory));
          }
        }

        if (change.length) assetData.changeStandaloneAccessories = change;
        if (remove.length) assetData.removeStandaloneAccessories = remove;
      }

      if (asset.software.length) {
        assetData.software = asset.software;
      }

      if (asset.bundledAccessories.length) {
        const changes = asset.bundledAccessories
          .filter(it => it.changed && it.productNumber)
          .map(it => ({productNumber: it.productNumber, serialNumber: it.serialNumber || null}));

        if (changes.length) assetData.changedBundledAccessorySerialNumbers = changes;
      }
    } else {
      for (const key of AssetInputGqlModel.removalKeys) {
        if (asset.formGroup.controls[key].dirty) {
          assetData[key] = asset[key];
        }
      }
    }

    for (const key of AssetInputGqlModel.dorDataFields) {
      assetData[key] = asset[key];
    }

    return assetData;
  }
}
